import React from "react";
import { Link } from "react-router-dom";
import { CaretRight, Cpu } from "@phosphor-icons/react";
import { useT } from "../../lib/i18n";

export const REVIEW_STATUSES = ["pending", "review", "issues", "reviewed"];

const STATUS_STYLE = {
  pending: "border-zinc-200 bg-zinc-50 text-zinc-700",
  review: "border-blue-200 bg-blue-50 text-blue-800",
  issues: "border-red-200 bg-red-50 text-red-700",
  reviewed: "border-emerald-200 bg-emerald-50 text-emerald-800",
};

export function ReviewStatus({ value = "pending" }) {
  const t = useT();
  const status = REVIEW_STATUSES.includes(value) ? value : "pending";
  return (
    <span className={`inline-flex items-center gap-1 border rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[status]}`} data-testid={`review-status-${status}`}>
      {t(`reg.status.${status}`)}
    </span>
  );
}

export function DevicePicker({ devices = [], value, onChange, testid = "reg-device-picker" }) {
  const t = useT();
  if (!devices.length) return null;
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{t("reg.select_device")}</span>
      <select value={value || ""} onChange={(event) => onChange(event.target.value)} data-testid={testid}
        className="w-full max-w-xl border border-zinc-200 bg-white px-3 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none">
        {devices.map((device) => (
          <option key={device.id} value={device.id}>
            {device.name}{device.model ? ` · ${device.model}` : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

export function NoProjectDevices({ projectId }) {
  const t = useT();
  return (
    <div className="grid-card p-8 text-center" data-testid="reg-no-devices">
      <Cpu size={24} className="mx-auto text-zinc-300" />
      <p className="text-sm text-zinc-600 mt-3">{t("reg.no_devices")}</p>
      <Link to={`/projects/${projectId}/devices/new`} className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-blue-700 hover:underline">
        {t("reg.add_device")} <CaretRight size={14} />
      </Link>
    </div>
  );
}

export function ModuleHeader({ title, subtitle, status, icon: Icon }) {
  return (
    <div className="grid-card p-5 sm:p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && <Icon size={22} weight="duotone" className="text-blue-700 shrink-0 mt-0.5" />}
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-extrabold tracking-tight">{title}</h2>
            <p className="text-sm text-zinc-500 mt-1 max-w-3xl">{subtitle}</p>
          </div>
        </div>
        <ReviewStatus value={status} />
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", placeholder = "", testid, disabled = false }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-1">{label}</span>
      <input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} placeholder={placeholder}
        data-testid={testid} disabled={disabled}
        className="w-full border border-zinc-200 bg-white px-3 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none disabled:bg-zinc-50 disabled:text-zinc-500" />
    </label>
  );
}

export function TextArea({ label, value, onChange, placeholder = "", rows = 3, testid }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-1">{label}</span>
      <textarea value={value ?? ""} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} rows={rows}
        data-testid={testid}
        className="w-full border border-zinc-200 bg-white px-3 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none resize-y" />
    </label>
  );
}

export function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block min-w-0">
      <span className="block text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-1">{label}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value)} data-testid={testid}
        className="w-full border border-zinc-200 bg-white px-3 py-2 rounded-sm text-sm focus:border-blue-600 focus:outline-none">
        {options.map(([option, labelText]) => <option key={option} value={option}>{labelText}</option>)}
      </select>
    </label>
  );
}

export function SectionCard({ title, subtitle, children, testid }) {
  return (
    <section className="grid-card p-5 sm:p-6 space-y-4" data-testid={testid}>
      <div>
        <h3 className="text-xs font-mono uppercase tracking-widest text-blue-800 font-bold">{title}</h3>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}
